/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

import { PublicClientApplication, Configuration, AuthenticationResult } from '@azure/msal-browser';
import { showMessage } from "./notification.service";
import { environment } from '../config/environment';

/* global OfficeRuntime */

let msalInstance: PublicClientApplication | null = null;

// MSAL configuration using environment settings
const msalConfig: Configuration = {
  auth: {
    clientId: environment.auth.clientId,
    authority: environment.auth.authority,
    redirectUri: environment.urls.redirectUri
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false
  }
};

function getMsalInstance(): PublicClientApplication {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
}

export async function getUserData(): Promise<any> {
  try {
    const accessToken = await getAccessTokenForGraph();
    // Request officeLocation to use for filtering organization users by location
    return await callMSGraph(accessToken, 'https://graph.microsoft.com/v1.0/me?$select=id,displayName,mail,userPrincipalName,officeLocation');
  } catch (exception) {
    console.error('getUserData error:', exception);
    showMessage(`Failed to get user data: ${(exception as Error).message}`);
    throw exception;
  }
}

export async function getAccessTokenForGraph(): Promise<string> {
  return await authenticateWithMSAL(environment.auth.scopes.graph);
}

export async function getAccessTokenForAPI(): Promise<string> {
  return await authenticateWithMSAL(environment.auth.scopes.api);
}

async function authenticateWithMSAL(scopes: string[]): Promise<string> {
  try {
    const msal = getMsalInstance();
    await msal.initialize();

    const accounts = msal.getAllAccounts();
    
    const tokenRequest = {
      scopes: scopes,
      account: accounts.length > 0 ? accounts[0] : undefined
    };

    let authResult: AuthenticationResult;

    if (accounts.length > 0) {
      // Try silent token acquisition first
      try {
        authResult = await msal.acquireTokenSilent(tokenRequest);
      } catch (silentError) {
        console.log('Silent token acquisition failed, trying popup:', silentError);
        authResult = await msal.acquireTokenPopup(tokenRequest);
      }
    } else {
      // No accounts, use popup
      authResult = await msal.acquireTokenPopup(tokenRequest);
    }

    if (authResult && authResult.accessToken) {
      return authResult.accessToken;
    } else {
      throw new Error('Failed to acquire access token');
    }
  } catch (error) {
    console.error('MSAL authentication failed:', error);
    showMessage(`Authentication failed: ${(error as Error).message}`);
    throw error;
  }
}

// Call Microsoft Graph API
async function callMSGraph(accessToken: string, url: string): Promise<any> {
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': 'Bearer ' + accessToken,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error('Graph API Error:', response.status, response.statusText, errorBody);
      throw new Error(`Microsoft Graph API error: ${response.status} ${response.statusText} - ${errorBody}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error calling Microsoft Graph:', error);
    throw error;
  }
}


